import * as dateFns from 'date-fns';
import * as jstz from 'jstimezonedetect';
import * as moment from 'moment-timezone';

export const getLocalTimezoneAbbr = getLocalTimezoneAbbrFn;
export const formatMomentDate = formatMomentDateFn;

export function tryGetStartOfDayISO(date: Date | undefined | null): string | undefined {
  if (date != null) {
    return dateFns.startOfDay(date).toISOString();
  }
  return undefined;
}

export function tryGetEndOfDayISO(date: Date | undefined | null): string | undefined {
  if (date != null) {
    return dateFns.endOfDay(date).toISOString();
  }
  return undefined;
}

export function tryParseDate(value: string | null | undefined) {
  if (value == null) {
    return undefined;
  }
  const date = dateFns.parse(value);
  if (dateFns.isValid(date)) {
    return date;
  }
  return undefined;
}

export function parseDateOrDefault(value: any): Date | undefined {
  if (value == null) {
    return undefined;
  }
  const date: Date = dateFns.isDate(value) ? value : new Date(value);
  return dateFns.isValid(date) ? date : undefined;
}

export function getDateOrDefault(value: any): Date | undefined {
  const valid = isDate(value);
  return valid ? (value as Date) : undefined;
}

function formatMomentDateFn(
  date: Date | number | string | null | undefined,
  format: string,
): string | undefined {
  if (date == null) {
    return undefined;
  }
  date = toDate(date);
  return isDate(date) ? moment(date).format(format) : String(date);
}

export function diffDates(start: Date, end: Date): number {
  return isDate(start) && isDate(end) ? Math.abs(end.getTime() - start.getTime()) : 0;
}

export function getDurationInMs(start: Date, end: Date): number {
  return end.getTime() - start.getTime();
}

export function getDurationInMsOrDefault(
  start: Date | undefined,
  end: Date | undefined,
): number | undefined {
  return start != null && end != null ? getDurationInMs(start, end) : undefined;
}

export function isDate(date: any): date is Date {
  return date instanceof Date && !isNaN(date as any);
}

export function toDate(date: Date | number | string): Date {
  if (typeof date === 'string') {
    date = new Date(Date.parse(date as string));
  } else if (typeof date === 'number') {
    date = new Date(date);
  }
  return date;
}

export function hasMinimumYear(date: any, minYear: number = 2016) {
  return isDate(date) && date.getFullYear() >= minYear;
}

function getLocalTimezoneAbbrFn() {
  // A pure JS version but not perfect. ref: http://stackoverflow.com/a/34405528
  // let zone = new Date().toLocaleTimeString('en-us',
  // {timeZoneName:'short'}).split(' ')[2] going with moment, moment-timezone,
  // and jsTimezoneDetect.
  const tz = jstz.determine();
  const abbrTz = moment.tz(new Date(), tz.name()).format('z');
  return abbrTz;
}

// https://stackoverflow.com/questions/28020805/regex-validate-correct-iso8601-date-string-with-time
const regexIso8601 = /^(?:[1-9]\d{3}-(?:(?:0[1-9]|1[0-2])-(?:0[1-9]|1\d|2[0-8])|(?:0[13-9]|1[0-2])-(?:29|30)|(?:0[13578]|1[02])-31)|(?:[1-9]\d(?:0[48]|[2468][048]|[13579][26])|(?:[2468][048]|[13579][26])00)-02-29)T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:Z|[+-][01]\d:[0-5]\d)$/;

export const kendoDateTime24HourFormat = 'MM/dd/y HH:mm';

export function convertDateStringsToDates(input: { [id: string]: any }) {
  // Ignore things that aren't objects.
  if (typeof input !== 'object') {
    return input;
  }

  for (const key in input) {
    if (!input.hasOwnProperty(key)) {
      continue;
    }

    const value = input[key];
    // Check for string properties which look like dates.
    if (typeof value === 'string' && isNaN(+value)) {
      try {
        if (value.endsWith('Z') && value[10] === 'T') {
          const parsed = dateFns.parse(value);
          if (dateFns.isDate(parsed) && isDate(parsed)) {
            input[key] = parsed;
          }
        }
      } catch {}
    } else if (typeof value === 'object') {
      // Recurse into object
      convertDateStringsToDates(value);
    }
  }
}

export const millisecondsInHour = 3600 * 1000;
export const millisecondsInMinute = 60 * 1000;

export function convertMillisecondsToHours(milliseconds: number) {
  return milliseconds / millisecondsInHour;
}

export function convertHoursToMilliseconds(hours: number) {
  return millisecondsInHour * hours;
}
